/**
 * Australian transfer (stamp) duty — general residential schedules per
 * state/territory, plus first-home-buyer concessions for the states that grant
 * a duty concession by price.
 *
 * These are the published *general rate* schedules (owner-occupier standard
 * transfer duty). They are an INDICATIVE ESTIMATE only — the exact figure
 * depends on the buyer's full circumstances (foreign-purchaser surcharge,
 * off-the-plan concessions, pensioner/PPR concessions, new-vs-established, and
 * annual CPI indexation in some states). Always confirm with the relevant state
 * revenue office. Verified against known bracket boundaries in stampDuty.test.ts.
 *
 * Confidence: VIC/NSW/QLD/SA/WA/TAS/NT schedules are standard and stable.
 * ACT conveyance rates change yearly and income-tested concessions can reduce
 * them to nil — treat ACT as the roughest of the set.
 */

export type AustralianState = 'NSW' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'ACT' | 'NT'

/** A progressive bracket: for value in (over, upTo], duty = base + rate·(value − over). */
interface Bracket {
  upTo: number
  base: number
  rate: number
  over: number
}

const SCHEDULES: Record<Exclude<AustralianState, 'NT'>, Bracket[]> = {
  VIC: [
    { upTo: 25_000, base: 0, rate: 0.014, over: 0 },
    { upTo: 130_000, base: 350, rate: 0.024, over: 25_000 },
    { upTo: 960_000, base: 2_870, rate: 0.06, over: 130_000 },
    { upTo: 2_000_000, base: 0, rate: 0.055, over: 0 }, // flat 5.5% of total
    { upTo: Infinity, base: 110_000, rate: 0.065, over: 2_000_000 },
  ],
  NSW: [
    { upTo: 17_000, base: 0, rate: 0.0125, over: 0 },
    { upTo: 37_000, base: 212, rate: 0.015, over: 17_000 },
    { upTo: 99_000, base: 512, rate: 0.0175, over: 37_000 },
    { upTo: 372_000, base: 1_597, rate: 0.035, over: 99_000 },
    { upTo: 1_240_000, base: 11_152, rate: 0.045, over: 372_000 },
    { upTo: 3_721_000, base: 50_212, rate: 0.055, over: 1_240_000 },
    { upTo: Infinity, base: 186_662, rate: 0.07, over: 3_721_000 }, // premium
  ],
  QLD: [
    { upTo: 5_000, base: 0, rate: 0, over: 0 },
    { upTo: 75_000, base: 0, rate: 0.015, over: 5_000 },
    { upTo: 540_000, base: 1_050, rate: 0.035, over: 75_000 },
    { upTo: 1_000_000, base: 17_325, rate: 0.045, over: 540_000 },
    { upTo: Infinity, base: 38_025, rate: 0.0575, over: 1_000_000 },
  ],
  SA: [
    { upTo: 12_000, base: 0, rate: 0.01, over: 0 },
    { upTo: 30_000, base: 120, rate: 0.02, over: 12_000 },
    { upTo: 50_000, base: 480, rate: 0.03, over: 30_000 },
    { upTo: 100_000, base: 1_080, rate: 0.035, over: 50_000 },
    { upTo: 200_000, base: 2_830, rate: 0.04, over: 100_000 },
    { upTo: 250_000, base: 6_830, rate: 0.0425, over: 200_000 },
    { upTo: 300_000, base: 8_955, rate: 0.0475, over: 250_000 },
    { upTo: 500_000, base: 11_330, rate: 0.05, over: 300_000 },
    { upTo: Infinity, base: 21_330, rate: 0.055, over: 500_000 },
  ],
  WA: [
    { upTo: 120_000, base: 0, rate: 0.019, over: 0 },
    { upTo: 150_000, base: 2_280, rate: 0.0285, over: 120_000 },
    { upTo: 360_000, base: 3_135, rate: 0.038, over: 150_000 },
    { upTo: 725_000, base: 11_115, rate: 0.0475, over: 360_000 },
    { upTo: Infinity, base: 28_453, rate: 0.0515, over: 725_000 },
  ],
  TAS: [
    { upTo: 3_000, base: 50, rate: 0, over: 0 },
    { upTo: 25_000, base: 50, rate: 0.0175, over: 3_000 },
    { upTo: 75_000, base: 435, rate: 0.0225, over: 25_000 },
    { upTo: 200_000, base: 1_560, rate: 0.035, over: 75_000 },
    { upTo: 375_000, base: 5_935, rate: 0.04, over: 200_000 },
    { upTo: 725_000, base: 12_935, rate: 0.0425, over: 375_000 },
    { upTo: Infinity, base: 27_810, rate: 0.045, over: 725_000 },
  ],
  ACT: [
    { upTo: 260_000, base: 0, rate: 0.0049, over: 0 },
    { upTo: 300_000, base: 1_274, rate: 0.022, over: 260_000 },
    { upTo: 500_000, base: 2_154, rate: 0.034, over: 300_000 },
    { upTo: 750_000, base: 8_954, rate: 0.0432, over: 500_000 },
    { upTo: 1_000_000, base: 19_754, rate: 0.059, over: 750_000 },
    { upTo: 1_455_000, base: 34_504, rate: 0.064, over: 1_000_000 },
    { upTo: Infinity, base: 0, rate: 0.0454, over: 0 }, // flat 4.54% of total
  ],
}

/** NT uses a quadratic formula below $525k, then flat rates. */
function ntDuty(value: number): number {
  if (value <= 525_000) {
    const v = value / 1000
    return 0.06571441 * v * v + 15 * v
  }
  if (value <= 3_000_000) return 0.0495 * value
  if (value <= 5_000_000) return 0.0575 * value
  return 0.0595 * value
}

/** General (non-concession) transfer duty for a state at a given dutiable value. */
function generalDuty(value: number, state: AustralianState): number {
  if (state === 'NT') return ntDuty(value)
  for (const b of SCHEDULES[state]) {
    if (value <= b.upTo) return b.base + b.rate * (value - b.over)
  }
  return 0
}

/**
 * First-home-buyer duty after the state's price-based concession. Only states
 * that grant a *duty* concession keyed to price are modelled; where relief is
 * income-tested or new-build-only (SA/ACT/NT) we return full general duty and
 * lean on the disclaimer, rather than understating the figure.
 */
function firstHomeDuty(value: number, state: AustralianState, general: number): number {
  switch (state) {
    case 'VIC': // exempt ≤600k, linear phase-in 600k→750k, none above
      if (value <= 600_000) return 0
      if (value <= 750_000) return (general * (value - 600_000)) / 150_000
      return general
    case 'NSW': // FHBAS: exempt ≤800k, taper 800k→1M
      if (value <= 800_000) return 0
      if (value <= 1_000_000) return (general * (value - 800_000)) / 200_000
      return general
    case 'QLD': // exempt ≤700k, taper 700k→800k
      if (value <= 700_000) return 0
      if (value <= 800_000) return (general * (value - 700_000)) / 100_000
      return general
    case 'WA': // metro: exempt ≤430k, taper 430k→530k
      if (value <= 430_000) return 0
      if (value <= 530_000) return (general * (value - 430_000)) / 100_000
      return general
    case 'TAS': // 50% concession for established homes ≤750k
      if (value <= 750_000) return general * 0.5
      return general
    default: // SA / ACT / NT — relief is income/new-build tested; keep full duty
      return general
  }
}

/**
 * Estimated transfer (stamp) duty in dollars. Indicative only — confirm with the
 * state revenue office.
 */
export function estimateStampDuty(
  price: number,
  state: AustralianState,
  firstHome: boolean,
): number {
  if (price <= 0) return 0
  const general = generalDuty(price, state)
  const duty = firstHome ? firstHomeDuty(price, state, general) : general
  return Math.round(Math.max(0, duty))
}

/** Official state/territory duty calculator, for the "confirm the exact figure" link. */
export const SRO_CALCULATORS: Record<AustralianState, string> = {
  VIC: 'https://www.sro.vic.gov.au/calculators/land-transfer-calculator',
  NSW: 'https://www.apps09.revenue.nsw.gov.au/erevenue/calculators/landsalesimple.php',
  QLD: 'https://www.qld.gov.au/housing/buying-owning-home/transfer-duty-estimator',
  SA: 'https://www.revenuesa.sa.gov.au/stampduty/stamp-duty-calculator',
  WA: 'https://www.wa.gov.au/organisation/department-of-finance/transfer-duty-assessment',
  TAS: 'https://www.sro.tas.gov.au/property-transfer-duties/calculator',
  ACT: 'https://www.revenue.act.gov.au/duties/conveyance-duty',
  NT: 'https://nt.gov.au/employ/money-and-taxes/taxes,-royalties-and-grants/stamp-duty',
}
